import * as React from 'react'
import cx from 'clsx'
import { Spin, Table } from 'antd'
import { camelCase, isEmpty, get, pick, debounce } from 'lodash'
import {
  getChildColumns,
  getTableWidth,
  showTotal,
  pageSizeOptions,
  createHandleColumnResize,
  createChildListViewHandleTableChange,
} from 'helpers/listViews'
import { showError } from 'helpers/errors'
import { getKeyValueItem, setKeyValueItem } from 'helpers/keyValueStorage'
import { createResetState } from 'helpers/utils'
import { tableCellComponents } from 'helpers/listItems'
import { t } from 'helpers/i18n'
import linkTargets from 'options/linkTargets'
import ResizableCell from 'elements/ResizableCell'
import Drawer from 'elements/Drawer'
import UnderDevelopment from 'elements/UnderDevelopment'

const getStorageKey = (self) => `childListItems.${camelCase(self.props.listType ?? 'Product')}`
const getListItemMode = (self) =>
  `${self.props.listType ?? 'Product'}List${self.props.entityType ?? 'Job'}Record`

class Component extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleColumnResize = createHandleColumnResize(this)
    this.handleTableChange = createChildListViewHandleTableChange(this)
    this.fetchChildItems = debounce(this.fetchChildItems, 500)
    this.resetState = createResetState(this, {
      columnWidths: {},
      fieldSettings: [],
      sortOrder: 'ascend',
      sortBy: 'barcode',
      pageSize: 20,
    })
    this.saveState = debounce(this.saveState, 2500)
  }

  async componentDidMount() {
    const savedState = await getKeyValueItem(getStorageKey(this), {})
    await this.resetState(savedState)
    await this.fetchSettings()
  }

  componentWillUnmount() {
    this.saveState.flush()
  }

  async componentDidUpdate(prevProps) {
    if (getListItemMode(this) !== getListItemMode({ props: prevProps })) {
      const savedState = await getKeyValueItem(getStorageKey(this), {})
      await this.resetState(savedState)
      await this.fetchSettings()
    }

    if (this.props.listId !== prevProps.listId) {
      await this.fetchChildItems()
    }

    if (this.props.locationId !== prevProps.locationId) {
      await this.fetchChildItems()
    }
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  fetchSettings = async () => {
    try {
      const { getSettings, listItemMode = getListItemMode(this) } = this.props
      const response = await getSettings({ type: camelCase(listItemMode) })

      this.setState({ fieldSettings: response.value.data.fieldSettings }, this.fetchChildItems)
    } catch (error) {
      showError({ error })
    }
  }

  fetchChildItems = async () => {
    try {
      const {
        locationId,
        domainObjectType,
        listId,
        listType = 'Product',
        listItemMode = getListItemMode(this),
      } = this.props

      this.setState({ loading: true })

      const response = await this.props.getChildItems(listId, {
        listId,
        domainObjectType,
        locationId,
        listType,
        listItemMode,
      })

      this.setState({ items: response.value.data.items })

      return response
    } catch (error) {
      showError({ error })

      return error
    } finally {
      this.setState({ loading: false })
    }
  }

  saveState = () => setKeyValueItem(getStorageKey(this), pick(this.state, ['columnWidths', 'pageSize']))

  handleLinkTargetClick = ({ column, record, text, readOnly }) => {
    if (process.env.NODE_ENV === 'development') console.log({ column, record, text, readOnly })

    const editDrawerLinkTarget = linkTargets[column.linkTarget]
    const { drawerTitleLanguageKey } = editDrawerLinkTarget ?? {}

    this.setState({
      editDrawerVisible: true,
      editDrawerSaving: false,
      editDrawerLinkTarget,
      editDrawerLinkTargetRecord: record,
      editDrawerLinkTargetText: drawerTitleLanguageKey ? `${t(drawerTitleLanguageKey)} - ${text}` : text,
      editDrawerReadOnly: false,
    })
  }

  handleDrawerClose = () =>
    this.setState({
      editDrawerVisible: false,
      editDrawerSaving: false,
      editDrawerLinkTarget: null,
      editDrawerLinkTargetRecord: null,
      editDrawerLinkTargetText: '',
    })

  render() {
    const { sortOrder, sortBy, pageSize, items = [], fieldSettings = [], columnWidths = {} } = this.state

    if (isEmpty(fieldSettings)) {
      return <Spin />
    }

    const columns = getChildColumns({
      sortBy,
      sortOrder,
      fieldSettings,
      columnWidths,
      tableCellComponents,
      onResize: this.handleColumnResize,
      onClick: this.handleLinkTargetClick,
      self: this,
    })
    const width = isEmpty(columns) ? '100%' : getTableWidth(columns)
    const EditComponent = get(this.state, 'editDrawerLinkTarget.formComponent', UnderDevelopment)

    // console.log({ items, fieldSettings })

    return (
      <>
        <div className={cx('tofino-child-list-view', { 'tofino-table-not-empty': !isEmpty(items) })}>
          <div className="tofino-table-container">
            <Table
              childrenColumnName={[]}
              rowKey={(record) => record.id}
              loading={this.state.loading}
              columns={columns}
              dataSource={items}
              size="middle"
              style={{ width }}
              components={{ header: { cell: ResizableCell } }}
              pagination={{
                pageSize,
                showTotal,
                pageSizeOptions,
                showSizeChanger: true,
              }}
              onChange={this.handleTableChange}
            />
          </div>
        </div>
        <Drawer
          title={this.state.editDrawerLinkTargetText}
          size={this.state.editDrawerLinkTarget?.formSize}
          visible={this.state.editDrawerVisible}
          onClose={this.handleDrawerClose}
          saving={this.state.editDrawerSaving}
        >
          <EditComponent
            linkTargetRecord={this.state.editDrawerLinkTargetRecord}
            readOnly={this.state.editDrawerReadOnly}
            onSave={(pending) =>
              this.promiseState({ editDrawerSaving: pending }).then(async () => {
                if (!pending) {
                  await this.fetchChildItems()
                }
              })
            }
            onSaveAndClose={this.handleDrawerClose}
            onCancel={this.handleDrawerClose}
          />
        </Drawer>
      </>
    )
  }
}

export default Component
